/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  isOperationalOrHigher,
  isBrokerOrHigher,
  type UserRole,
} from '@shared/utils/permissions';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { CalendarEvent } from '@services/requests/housien-models/CalendarEvent';

/**
 * Whether the role can create/edit/delete institutional (`owner_type: agency`)
 * calendars. Only operational and above.
 *
 * @param role The current user role.
 */
export const canManageInstitutionalCalendar = (role: UserRole): boolean =>
  isOperationalOrHigher(role);

/**
 * Whether the role can manage a given calendar. Personal calendars are
 * manageable by any non-anonymous member; institutional ones require
 * operational+.
 *
 * @param role The current user role.
 * @param calendar The calendar in question.
 */
export const canManageCalendar = (
  role: UserRole,
  calendar: Calendar
): boolean => {
  if (calendar.owner_type === 'agency') {
    return canManageInstitutionalCalendar(role);
  }
  return isBrokerOrHigher(role);
};

/**
 * Whether the event was created by the given user.
 * @param event - The calendar event.
 * @param userEmail - The current user's email.
 * @returns True when the organizer email matches the user email.
 */
const isOwnEvent = (event: CalendarEvent, userEmail: string): boolean =>
  (event.organizer_email ?? '').toLowerCase() === userEmail.toLowerCase();

/**
 * Whether the role can manage an event. Operational and above can manage any
 * event; brokers can only manage events they organize.
 *
 * @param role The current user role.
 * @param event The event in question.
 * @param userEmail The current user email.
 */
export const canManageEvent = (
  role: UserRole,
  event: CalendarEvent,
  userEmail: string
): boolean => {
  if (isOperationalOrHigher(role)) {
    return true;
  }
  if (role === 'broker') {
    return isOwnEvent(event, userEmail);
  }
  return false;
};
